import { BrowserRouter } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Providers from "@/providers";
import AppRoutes from "@/routes";
import CommandBar from "@/components/CommandBar";
import CommandPalette from "@/components/CommandPalette";
import QuickCapture from "@/components/QuickCapture";
import ShortcutOverlay from "@/components/ShortcutOverlay";
import OnboardingFlow from "@/components/OnboardingFlow";
import Tutorial from "@/components/Tutorial";
import CmdKHintPulse from "@/components/CmdKHintPulse";
import FirstTaskCelebrate from "@/components/FirstTaskCelebrate";
import DailyActiveTracker from "@/components/DailyActiveTracker";
import AppearanceApplier from "@/components/AppearanceApplier";
import ScreenSaver from "@/components/ScreenSaver";
import WhatsNewModal from "@/components/WhatsNewModal";
import LevelUpModal from "@/components/LevelUpModal";
import OfflineIndicator from "@/components/OfflineIndicator";
import InstallPrompt from "@/components/InstallPrompt";
import ShadowOwnerPanel from "@/components/ShadowOwnerPanel";
import TwoFactorEnrolGate from "@/components/TwoFactorEnrolGate";
import BetaWelcomeModal from "@/components/BetaWelcomeModal";
import FeedbackWidget from "@/components/FeedbackWidget";
import PushPermission from "@/components/PushPermission";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import InvitesPanel from "@/components/InvitesPanel";
import StealthWatermark from "@/components/StealthWatermark";
import AnalyticsPageTracker from "@/components/AnalyticsPageTracker";
import LegalFooter from "@/components/LegalFooter";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import { can } from "@/lib/roles";
import "@/App.css";

function AppShell() {
  const { user } = useAuth();
  return (
    <BrowserRouter>
      <AnalyticsPageTracker />
      <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[999] focus:px-4 focus:py-2 focus:rounded-lg focus:text-sm focus:font-semibold"
        style={{ background: "#A855F7", color: "#fff" }} data-testid="skip-to-content">
        Skip to main content
      </a>
      {user && <CommandBar />}
      <main id="main-content" role="main" style={{ paddingTop: user ? "var(--header-h)" : "0" }}>
        {user && <VerifyEmailBanner />}
        <AppRoutes />
      </main>
      {user && <LegalFooter />}
      {user && <ScreenSaver />}
      {user && <LevelUpModal />}
      {user && <WhatsNewModal />}
      <OfflineIndicator />
      <InstallPrompt />
      {can(user, "manage_app") && <ShadowOwnerPanel />}
      {user?.two_factor_setup_required && <TwoFactorEnrolGate />}
      <BetaWelcomeModal />
      <FeedbackWidget />
      {user && <PushPermission />}
      {user && <InvitesPanel />}
      {user && <ShortcutOverlay />}
      {user && <CommandPalette />}
      {user && <QuickCapture />}
      {user && <OnboardingFlow />}
      {user && <Tutorial />}
      {user && <CmdKHintPulse />}
      {user && <FirstTaskCelebrate />}
      {user && <DailyActiveTracker />}
      {user && <AppearanceApplier />}
      <StealthWatermark />
      {!FEATURE_FLAGS.PAYWALL_ENABLED && (
        <div className="fixed bottom-2 left-2 z-[9999] text-xs pointer-events-none select-none"
          style={{ color: "rgba(255,255,255,0.4)" }} data-testid="paywall-dev-indicator">
          Paywall: OFF (dev)
        </div>
      )}
      <Toaster position="bottom-right" />
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <Providers>
      <AppShell />
    </Providers>
  );
}
